<script setup lang="ts">
import { watchImmediate } from '@vueuse/core';
import { getArticleCounter, updateArticleCounter } from '@waline/api';
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';

import { useReactionStorage } from '../composables/index.js';
import { configKey } from '../config/index.js';
import type { WalineReactionLocale } from '../typings/index.js';
import { LoadingIcon } from './Icons.js';

interface ReactionItem {
  icon: string;
  desc: string;
  active?: boolean;
}

const reactionStorage = useReactionStorage();
// oxlint-disable-next-line typescript/no-non-null-assertion
const config = inject(configKey)!;

const votingIndex = ref(-1);
const voteNumbers = ref<number[]>([]);

const locale = computed(() => config.value.locale);
const reaction = computed(() => {
  const { reaction } = config.value;

  return reaction?.length ? reaction : null;
});

const reactionsInfo = computed<ReactionItem[] | null>(() => {
  const { path } = config.value;

  return (
    reaction.value?.map((icon, index) => ({
      icon,
      desc: locale.value[`reaction${index}` as keyof WalineReactionLocale],
      active: reactionStorage.value[path] === index,
    })) ?? null
  );
});

let abort: (() => void) | undefined;

const fetchReaction = async (): Promise<void> => {
  const { serverURL, lang, path } = config.value;

  if (!reaction.value) {
    return;
  }

  const controller = new AbortController();

  abort = controller.abort.bind(controller);

  try {
    const [reactionData] = (await getArticleCounter({
      serverURL,
      lang,
      paths: [path],
      type: reaction.value.map((_, index) => `reaction${index}`),
      signal: controller.signal,
    })) as Record<string, number>[];

    voteNumbers.value = reaction.value.map((_, index) => reactionData[`reaction${index}`]);
  } catch (err) {
    // Ignore aborted requests, but keep the widget usable after network errors.
    if (err instanceof Error && err.name === 'AbortError') return;

    // oxlint-disable-next-line no-console
    console.error('[Waline] Failed to fetch reaction data', err);
  }
};

const vote = async (index: number): Promise<void> => {
  // we should ensure that only one vote request is sent at a time
  if (votingIndex.value !== -1) {
    return;
  }

  const { serverURL, lang, path } = config.value;
  const currentVoteItemIndex = reactionStorage.value[path];
  const prevVoteNumbers = [...voteNumbers.value];

  // mark voting status
  votingIndex.value = index;

  try {
    // if user already vote current article, decrease the voted item number
    if (currentVoteItemIndex != null) {
      await updateArticleCounter({
        serverURL,
        lang,
        path,
        type: `reaction${currentVoteItemIndex}`,
        action: 'desc',
      });

      voteNumbers.value[currentVoteItemIndex] = Math.max(
        (voteNumbers.value[currentVoteItemIndex] ?? 0) - 1,
        0,
      );
    }

    // increase voting number if current reaction item is not been voted
    if (currentVoteItemIndex !== index) {
      await updateArticleCounter({
        serverURL,
        lang,
        path,
        type: `reaction${index}`,
      });
      voteNumbers.value[index] = (voteNumbers.value[index] ?? 0) + 1;
    }

    // update vote info in local storage
    if (currentVoteItemIndex === index) {
      // oxlint-disable-next-line typescript/no-dynamic-delete
      delete reactionStorage.value[path];
    } else {
      reactionStorage.value[path] = index;
    }
  } catch (err) {
    // Revert optimistic updates and keep the widget usable after a failed request.
    voteNumbers.value = prevVoteNumbers;

    if (currentVoteItemIndex == null) {
      // oxlint-disable-next-line typescript/no-dynamic-delete
      delete reactionStorage.value[path];
    } else {
      reactionStorage.value[path] = currentVoteItemIndex;
    }

    // oxlint-disable-next-line no-console
    console.error('[Waline] Failed to update reaction counter', err);
  } finally {
    votingIndex.value = -1;
  }
};

onMounted(() => {
  watchImmediate(
    () => [config.value.serverURL, config.value.path],
    () => fetchReaction(),
  );
});

onUnmounted(() => {
  abort?.();
});
</script>

<template>
  <div v-if="reactionsInfo" class="wl-reaction">
    <div class="wl-reaction-title" v-text="locale.reactionTitle" />

    <ul class="wl-reaction-list">
      <li
        v-for="({ active, icon, desc }, index) in reactionsInfo"
        :key="index"
        class="wl-reaction-item"
        :class="{ active }"
        @click="vote(index)"
      >
        <div class="wl-reaction-img">
          <img :src="icon" :alt="desc" />

          <LoadingIcon v-if="votingIndex === index" class="wl-reaction-loading" />

          <div v-else class="wl-reaction-votes" v-text="voteNumbers[index] || 0" />
        </div>

        <div class="wl-reaction-text" v-text="desc" />
      </li>
    </ul>
  </div>
</template>
